import { P } from '@dnb/eufemia';
import { DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { useNavHistory } from '@portals/shared-frontend/hooks';
import { useMemo } from 'react';

import { DragOverlayContainer } from './DragOverlayContainer';
import { PinnedPagesContainer } from './PinnedPagesContainer';
import { RecentPagesContainer } from './RecentPagesContainer';

export function PinnedAndRecentPagesView() {
  const { getPinnedAndRecentPages, pinPage, unpinPage } = useNavHistory();
  // Memoize the pages as getPinnedAndRecentPages loads and maps values which we don't need to do on every render
  const pinnedAndRecentPages = useMemo(
    () => getPinnedAndRecentPages().map((page) => page.node),
    [getPinnedAndRecentPages],
  );

  const { pinnedPages, recentPages } = useMemo(() => {
    const pinnedPages = pinnedAndRecentPages.filter((page) => page.pinned);
    const recentPages = pinnedAndRecentPages.filter((page) => !page.pinned);
    return { pinnedPages, recentPages };
  }, [pinnedAndRecentPages]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    if (over.id === 'pinned-pages-container' || pinnedPages.some((page) => page.path === over.id)) {
      let newIndex = pinnedAndRecentPages.findIndex((t) => t.path === over.id);
      if (newIndex === -1) {
        // Page was dropped in the pinned area but not on a specific item, so placing it at the end
        newIndex = pinnedPages.length;
      }
      pinPage(String(active.id), newIndex);
    } else if (over.id === 'recent-pages-container' || recentPages.some((page) => page.path === over.id)) {
      unpinPage(String(active.id));
    }
  }

  if (pinnedAndRecentPages.length === 0) {
    return <P size="small">Recent pages will be shown here. They can also be pinned</P>;
  }

  return (
    <DndContext modifiers={[restrictToHorizontalAxis]} onDragEnd={handleDragEnd} sensors={sensors}>
      <nav aria-label="Pinned and recent pages" className="flex gap-3 w-full" role="group">
        <PinnedPagesContainer pinnedPages={pinnedPages} recentPages={recentPages} />
        <div className="border-l-2 border-gray-300" />
        <RecentPagesContainer pinnedPages={pinnedPages} recentPages={recentPages} />
        <DragOverlayContainer pinnedAndRecentPages={pinnedAndRecentPages} />
      </nav>
    </DndContext>
  );
}
